import React, { useState } from 'react'
import { Popover, Grow } from '@mui/material'
import { AllInboxOutlined, Inventory2Outlined, MoveDownOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

/**
 * AssetResourcesButton — a single "Resources" touch button that unifies the two
 * resource actions the card used to expose as separate buttons (PO): "Physical
 * parts" and "Manage location". Instead of a dropdown LIST (poor touch target),
 * tapping it pops TWO large, touch-friendly buttons that grow into place (PO). Same
 * shared `actions`/permissions from `AssetCardDialogs` — behaviour unchanged.
 * Disabled when the user has neither permission.
 */
export default function AssetResourcesButton ({ actions, deviceId, large = false, hasResources = true }) {
  const { t_ } = useSmartTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  // "Physical parts" opens the per-resource quick actions, so it is disabled without the
  // permission OR when the device holds no physical resource (nothing to act on).
  const canParts = actions.isUserAuthorized('ASSET_RESOURCES') && hasResources
  const canLocation = actions.isUserAuthorized('ASSET_RESOURCE_MOVE')

  const run = (fn) => { setAnchorEl(null); fn() }

  return (
    <>
      <button
        type='button'
        data-testid={`asset-action-resources-${deviceId}`}
        className={`mcx-touch-btn${large ? ' mcx-touch-btn--lg' : ''}`}
        style={{ flex: 1 }}
        disabled={!canParts && !canLocation}
        onClick={(event) => { event.stopPropagation(); setAnchorEl(event.currentTarget) }}
      >
        <Inventory2Outlined sx={{ fontSize: large ? 32 : 22 }} />
        <span className='mcx-touch-btn__label'>{t_('Resources')}</span>
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        TransitionComponent={Grow}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible', mt: -1 } } }}
      >
        <div className='mcx-resource-pop'>
          <button
            type='button'
            data-testid={`asset-action-parts-${deviceId}`}
            className='mcx-touch-btn'
            disabled={!canParts}
            onClick={() => run(() => actions.openParts())}
          >
            <AllInboxOutlined sx={{ fontSize: 26 }} />
            <span className='mcx-touch-btn__label'>{t_('Physical parts')}</span>
          </button>
          <button
            type='button'
            data-testid={`asset-action-location-${deviceId}`}
            className='mcx-touch-btn'
            disabled={!canLocation}
            onClick={() => run(() => actions.manageLocation())}
          >
            <MoveDownOutlined sx={{ fontSize: 26 }} />
            <span className='mcx-touch-btn__label'>{t_('Manage location')}</span>
          </button>
        </div>
      </Popover>
    </>
  )
}
